import React from 'react';
import PropTypes from 'prop-types';

import TimeAgo from 'timeago-react';
import {register} from 'timeago.js';

import {
  Candytar,
  Grid,
  GridCell,
  Heading,
  Text,
  Separator,
  Strong,
} from '@brandwatch/axiom-components';
import {stringToColor} from '@brandwatch/axiom-utils';

import useMarkdown from '../hooks/useMarkdown.jsx';

import './InviteMessage.css';

register('custom-locale', (number, index) => {
  return [
    ['just now', 'right now'],
    ['%ss', 'in %ss'],
    ['1m', 'in 1m'],
    ['%sm', 'in %sm'],
    ['1h', 'in 1h'],
    ['%sh', 'in %sh'],
    ['1d', 'in 1d'],
    ['%sd', 'in %sd'],
    ['1w', 'in 1w'],
    ['%sw', 'in %sw'],
    ['1m', 'in 1m'],
    ['%sm', 'in %sm'],
    ['1y', 'in 1y'],
    ['%sy', 'in %sy']
  ][index];
});

const Avatar = ({creatorEmail, size}) => <Candytar color={stringToColor(creatorEmail)} size={size} />;

Avatar.propTypes = {
  creatorEmail: PropTypes.string.isRequired,
  size: PropTypes.string
};

Avatar.defaultProps = {
  size: '2rem'
};

const OtherInvitees = ({count}) => {
  if (count > 0) {
    return <Text> and {count} other{count > 1 ? 's' : ''}</Text>;
  }

  return null;
};

OtherInvitees.propTypes = {
  count: PropTypes.number.isRequired
};

export default function InviteMessage({deck, invite, inviteeCount, isMobile}) {
  const inviteCreatorName = invite.creator_first_name ? `${invite.creator_first_name} ${invite.creator_last_name}` : null;
  const otherInviteesCount = inviteeCount - 1;
  const deckName = deck && deck.name;

  const markdownMessage = useMarkdown(invite.message, 'p');

  if (!invite || !invite.invite_id || isMobile && !deckName) {
    return null;
  }

  if (isMobile) {
    return (
      <div className="InviteMessage ax-theme--night">
        <div className="InviteMessage__Avatar">
          <Avatar creatorEmail={invite.creator_email} size="3rem" />
        </div>
        <div className="InviteMessage__Intro">
          <Heading textSize="headtitle">
            {inviteCreatorName} shared the following deck with you
            <OtherInvitees count={otherInviteesCount} />
          </Heading>
        </div>
        <Heading space="x4" textSize="headline">{deckName}</Heading>
        <Text textColor="subtle">
          <TimeAgo datetime={invite.created_on} locale="custom-locale" live={false} /> ago
        </Text>
        {invite.message && (
          <React.Fragment>
            <Separator space="x8"/>
            <Text className="InviteMessage__message" space="x2" textColor="subtle" textSize="large">{markdownMessage}</Text>
          </React.Fragment>
        )}
      </div>
    );
  }

  return (
    <React.Fragment>
      <Grid horizontalGutters="tiny" verticalAlign="middle" space="x2" wrap={false} responsive={false}>
        <GridCell shrink={true}>
          <Avatar creatorEmail={invite.creator_email} />
        </GridCell>
        <GridCell>
          <Strong>{inviteCreatorName}</Strong>
          <Text> shared this Deck with you</Text>
          <OtherInvitees count={otherInviteesCount} />
          <Text textColor="subtle" textSize="small" style={{paddingLeft: '8px'}}>
            <TimeAgo datetime={invite.created_on} locale="custom-locale" live={false} />
          </Text>
        </GridCell>
      </Grid>
      {invite.message && <Text className="InviteMessage__message" space="x2">{markdownMessage}</Text>}
    </React.Fragment>
  );
}

InviteMessage.propTypes = {
  deck: PropTypes.object,
  invite: PropTypes.object,
  inviteeCount: PropTypes.number,
  isMobile: PropTypes.bool.isRequired
};

InviteMessage.defaultProps = {
  isMobile: false
};
